<template>
  <div class="floating-screen-container">
    <!-- 用户进房横幅 -->
    <template v-if="enterRoomInfo">
      <div
        class="enter-room flex v-c"
        :class="{
          enterRoomAnimation: isPlayingEnterRoom,
          [enterRoomWealthLevelClass]: true
        }"
        @animationend="enterRoomOnAnimationEnd"
      >
        <div class="floating-avatar flex flex-none v-c h-c">
          <van-image
            fit="cover"
            round
            :src="common.handlePicUrlAvatar(enterRoomInfo.avatar)"
          />
        </div>
        <div class="floating-text">
          {{ $t('gift.IAmHere', [enterRoomInfo.nick]) }}
        </div>
      </div>
      <!-- VIP7进房动画 -->
      <div
        v-if="(enterRoomInfo.vipId || 0) >= 7"
        class="user-info flex column v-c"
        :class="{ vip7EnterRoomAnimation: isPlayingEnterRoom }"
      >
        <div class="hw flex v-c h-c">
          <img :src="common.handlePicUrlAvatar(enterRoomInfo.avatar)" alt="" />
        </div>
        <div class="name flex v-c h-c">{{ enterRoomInfo.nick }}</div>
      </div>
    </template>

    <!-- 大额礼物横幅 -->
    <div
      v-if="largeGiftInfo"
      class="large-gift flex v-c"
      :class="[
        `large-gift-lv${largeGiftLevel}`,
        { floatingAnimation: isPlaying }
      ]"
      @animationend="onAnimationEnd"
    >
      <div class="floating-avatar flex v-c h-c">
        <van-image
          fit="cover"
          round
          :src="common.handlePicUrlAvatar(largeGiftInfo.avatar)"
        />
      </div>
      <div class="floating-text flex v-c s-b">
        <div class="floating-text-info">
          <span>{{ largeGiftInfo.nick }}</span>
          <br />
          {{ $t('gift.Send') }}
          <span>{{
            (largeGiftInfo.targetUids?.length || 0) > 1
              ? $t('gift.AllMic')
              : largeGiftInfo.targetUid
          }}</span>
          {{ largeGiftInfo.giftName }}
        </div>
        <div class="floating-text-count">x{{ largeGiftInfo.giftNum }}</div>
      </div>
      <div class="floating-gift">
        <img :src="largeGiftInfo.giftUrl" alt="" />
      </div>
    </div>

    <!-- 幸运礼物横幅 -->
    <div
      v-if="luckyGiftInfo"
      class="lucky-gift flex v-c"
      :class="[
        `lucky-gift-lv${luckyGiftLevel}`,
        { floatingAnimation: isPlaying }
      ]"
      @animationend="onAnimationEnd"
    >
      <div class="floating-avatar flex v-c h-c">
        <van-image
          fit="cover"
          round
          :src="common.handlePicUrlAvatar(luckyGiftInfo.avatar)"
        />
      </div>
      <div class="floating-text flex v-c s-b">
        <div
          class="floating-text-info"
          v-html="
            $t('gift.GiftBannerTips', {
              nick: `<span class='otw'>${luckyGiftInfo.nick}</span>`,
              gift: luckyGiftInfo.giftName,
              times: `<span>${luckyGiftInfo.proportion}</span>`,
              gold: `<span>${luckyGiftInfo.outputGold}</span>`
            })
          "
        ></div>
      </div>
    </div>

    <!-- 中部幸运礼物返币信息 -->
    <div
      v-show="giftBannerStore.luckyBackCoins"
      ref="luckyGiftBackCoinsRef"
      class="back-coins"
      :class="[
        `back-coins-lv${getLuckyGiftLevel(giftBannerStore.luckyBackCoins?.proportion || 0)}`,
        { scale: isPlayingLuckyBackCoins }
      ]"
      @animationend="onBackCoinsAnimationEnd"
    >
      <div class="get-coins flex column v-c h-c">
        <p>{{ $t('gift.Get') }}</p>
        <span>
          {{ giftBannerStore.luckyBackCoins?.outputGold }}
          {{ $t('gift.Coins') }}
        </span>
      </div>
      <div class="times flex v-c h-c">
        <p
          v-html="
            $t('gift.BackCoinsTips', [
              `<span>${giftBannerStore.luckyBackCoins?.proportion}</span>`
            ])
          "
        ></p>
      </div>
    </div>

    <PreviewEffect
      v-if="isPageVisible"
      ref="previewEffectRef"
      @on-start="onEffectStart"
      @on-end="onEffectEnd"
    />
  </div>
</template>

<!-- 横幅飘屏动画
  包括：大额礼物横幅, 幸运礼物横幅, 中部幸运礼物返币信息, 进房飘屏

  示例：
  import GiftBanner from '@/components/GiftBanner/index.vue'
  
  <GiftBanner />
 -->
<script lang="ts" setup>
defineOptions({ name: 'GiftBanner' })

import type { giftSendResponseInterface } from '@/types/apiType/giftType'
import type {
  luckyGiftBannerInterface,
  userEnterTheRoomBannerInterface
} from '~/types/viewsType/socketType'

import PreviewEffect from '~/components/PreviewEffect/PreviewEffect.vue'
const previewEffectRef = ref<InstanceType<typeof PreviewEffect>>()

const publicStore = usePublicStore()
const { isPageVisible } = storeToRefs(publicStore)

// giftBannerStore
const giftBannerStore = useGiftBannerStore()

// 大额礼物横幅
const largeGiftInfo = ref<giftSendResponseInterface | null>(null)
// 幸运礼物横幅
const luckyGiftInfo = ref<luckyGiftBannerInterface | null>(null)
// 用户进房横幅
const enterRoomInfo = ref<userEnterTheRoomBannerInterface | null>(null)

/**
 * 大额礼物横幅等级
 * @description
 * 总价
 * 大于等于30000 lv4
 * 大于等于9999 小于30000 lv3
 * 大于等于4999 小于9999 lv2
 * 大于等于520 小于4999 lv1
 */
const largeGiftLevel = computed(() => {
  if (!largeGiftInfo.value) return 0
  const totalPrice =
    largeGiftInfo.value.giftNum *
    largeGiftInfo.value.goldPrice *
    (largeGiftInfo.value.targetUids?.length ?? 1)
  if (totalPrice >= 30000) {
    return 4
  } else if (totalPrice >= 9999) {
    return 3
  } else if (totalPrice >= 4999) {
    return 2
  } else if (totalPrice >= 520) {
    return 1
  } else {
    return 0
  }
})

/**
 * 幸运礼物横幅等级
 * @description
 * 总价
 * 大于等于30000 lv4
 * 大于等于9999 小于30000 lv3
 * 大于等于4999 小于9999 lv2
 * 大于等于520 小于4999 lv1
 */
const luckyGiftLevel = computed(() => {
  if (!luckyGiftInfo.value) return 1
  return getLuckyGiftLevel(luckyGiftInfo.value.proportion)
})

/**
 * 进房横幅等级class
 * @description
 * 有VIP ? 返回 enter-room-vip-lv1 ~ 9
 * 财富等级
 * >=91 ? lv10
 * >=81 ? lv9
 * >=71 ? lv8
 * >=61 ? lv7
 * >=51 ? lv6
 * >=41 ? lv5
 * >=31 ? lv4
 * >=21 ? lv3
 * >=11 ? lv2
 * >=10 ? lv1
 * 否则空字符串 ''
 */
const enterRoomWealthLevelClass = computed(() => {
  if (!enterRoomInfo.value) return ''
  if (enterRoomInfo.value.vipId && enterRoomInfo.value.vipId > 0) {
    // vip
    return `enter-room-vip-lv${enterRoomInfo.value.vipId}`
  } else {
    // 财富等级
    const value = enterRoomInfo.value?.exper_level || 0
    if (value >= 91) return 'enter-room-wealth-lv10'
    if (value >= 81) return 'enter-room-wealth-lv9'
    if (value >= 71) return 'enter-room-wealth-lv8'
    if (value >= 61) return 'enter-room-wealth-lv7'
    if (value >= 51) return 'enter-room-wealth-lv6'
    if (value >= 41) return 'enter-room-wealth-lv5'
    if (value >= 31) return 'enter-room-wealth-lv4'
    if (value >= 21) return 'enter-room-wealth-lv3'
    if (value >= 11) return 'enter-room-wealth-lv2'
    if (value >= 10) return 'enter-room-wealth-lv1'
    return ''
  }
})

/**
 * 获取幸运礼物返币等级
 * @description
 * 对应等级档位 (0, 100],  (100, 500],  (500, 1000],  (1000, +∞)
 * @param times 倍数
 */
const getLuckyGiftLevel = (times: number) => {
  if (!times) return 1
  if (times <= 100) return 1
  if (times <= 500) return 2
  if (times <= 1000) return 3
  return 4
}

// 监听礼物横幅列表
watch(
  () => giftBannerStore.giftBanners,
  () => {
    playGiftBanner()
  },
  { deep: true }
)

// 监听进房横幅列表
watch(
  () => giftBannerStore.enterRoomBanners,
  () => {
    playEnterRoomBanner()
  },
  { deep: true }
)

const isPlaying = ref(false) // 礼物横幅是否播放中
// 播放礼物横幅飘屏
function playGiftBanner() {
  try {
    if (giftBannerStore.giftBanners.length > 0 && !isPlaying.value) {
      isPlaying.value = true
      // 取出第一个礼物信息
      const giftInfo = giftBannerStore.giftBanners[0]
      if (giftInfo.giftBannerViewType === GIFT_BANNER_TYPE.LUCKY) {
        // 播幸运礼物
        luckyGiftInfo.value = giftInfo
        largeGiftInfo.value = null
      } else {
        // 播大额礼物
        luckyGiftInfo.value = null
        largeGiftInfo.value = giftInfo
      }
      // 移除第一个礼物
      giftBannerStore.removeGiftBanner({ type: 'shift' })
    }
  } catch (error) {
    console.warn('playGiftBanner error', error)
    isPlaying.value = false
  }
}

const isPlayingEnterRoom = ref(false) // 礼物横幅是否播放中
// 播放礼物横幅飘屏
function playEnterRoomBanner() {
  try {
    if (
      giftBannerStore.enterRoomBanners.length > 0 &&
      !isPlayingEnterRoom.value
    ) {
      isPlayingEnterRoom.value = true
      // 取出第一个礼物信息
      const giftInfo = giftBannerStore.enterRoomBanners[0]
      enterRoomInfo.value = giftInfo
      // 移除第一个礼物
      giftBannerStore.removeEnterTheRoomBanner({ type: 'shift' })
    }
  } catch (error) {
    console.warn('playEnterRoomBanner error', error)
    isPlayingEnterRoom.value = false
  }
}

// 礼物横幅动画结束
const onAnimationEnd = () => {
  isPlaying.value = false
  setTimeout(() => {
    playGiftBanner()
  }, 0)
}

// 进房横幅动画结束
const enterRoomOnAnimationEnd = () => {
  isPlayingEnterRoom.value = false
  enterRoomInfo.value = null
  setTimeout(() => {
    playEnterRoomBanner()
  }, 0)
}

const luckyGiftBackCoinsRef = ref<HTMLDivElement>() // 返币的div实例
const isPlayingLuckyBackCoins = ref(false) // 返币动画是否播放中
// 监听返币信息变化时重新播放
watch(
  () => giftBannerStore.luckyBackCoins,
  (newValue) => {
    if (newValue) {
      playLuckyBackCoins()
    }
  },
  {
    deep: true
  }
)
/**
 * 播放中部的返币动画
 */
async function playLuckyBackCoins() {
  isPlayingLuckyBackCoins.value = false
  setTimeout(() => {
    isPlayingLuckyBackCoins.value = true
  }, 0)
}
// 返币动画结束
async function onBackCoinsAnimationEnd() {
  isPlayingLuckyBackCoins.value = false
  // 清除中部返币信息
  setTimeout(() => {
    giftBannerStore.setLuckyBackCoins(null)
  }, 0)
}

// 监听如果退出房间时清空横幅、返币信息
const roomStore = useRoomStore()
watch(
  () => roomStore.roomId,
  (newValue, oldValue) => {
    if (oldValue && !newValue) {
      giftBannerStore.removeGiftBanner({ type: 'all' })
      giftBannerStore.removeEnterTheRoomBanner({ type: 'all' })
      giftBannerStore.setLuckyBackCoins(null)
    }
  }
)

//#region 播放进房座驾特效
const rideEffectIsPlaying = ref(false)
// 播放动画礼物
async function playEffectGift() {
  try {
    // 页面显示才播
    if (
      giftBannerStore.rideEffects.length &&
      !rideEffectIsPlaying.value &&
      isPageVisible.value
    ) {
      rideEffectIsPlaying.value = true
      await nextTick()
      console.log('礼物特效开始播放')
      // 取出第一个礼物信息
      const giftSendInfo = giftBannerStore.rideEffects[0]
      // 移除第一个礼物
      giftBannerStore.removeEnterRideEffects({ type: 'shift' })

      // 先判断是否有mp4(vap)格式的，没有在播放svga
      // svga
      if (giftSendInfo.mp4Url) {
        // mp4(vap)
        previewEffectRef.value?.preview({
          vapUrl: giftSendInfo.mp4Url,
          objectFit: 'cover'
        })
      } else if (giftSendInfo.car_url) {
        // svga
        previewEffectRef.value?.preview({
          svgaUrl: cryptojs.decryptDES(
            giftSendInfo.car_url.replace(/[\r\n]/g, ''),
            cryptojs.effectsKey
          )
        })
      }
    }
  } catch (error) {
    rideEffectIsPlaying.value = false
  }
}

/**
 * 特效播放结束
 * 结束就执行一次播放
 * */
function onEffectEnd() {
  console.log('结束播放')
  rideEffectIsPlaying.value = false
  playEffectGift()
}

/**
 * 特效播放开始
 * */
function onEffectStart() {}

// 监听特效礼物列表
watch(
  () => giftBannerStore.rideEffects,
  () => {
    playEffectGift()
  },
  { deep: true }
)
// 监听特效礼物列表
watch(
  () => giftBannerStore.rideEffects,
  () => {
    playEffectGift()
  },
  { deep: true }
)

//#endregion
</script>

<style lang="scss">
@import './scss/index.scss';
</style>
